import useGetLineTypeName from "@/use/useGetLineTypeName";
import { IBookDetailInfo, IBookInfo } from "@/domain/entities/book";
import { ParseError } from "@/repositories/shared/errors/parseError";
import { Result, err, ok } from "neverthrow";
import {
  HttpBookDetailInfo,
  HttpBookInfo,
  IBookInfoParser,
} from "./../bookRepository.types";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { parseSeatsInfo } from "./seatsInfoParser";
import { injectable } from "inversify";
import { BookStatusName } from "@/types";
import { BookInfo } from "@/domain/entities/book/bookInfo";

dayjs.extend(duration);

function parseBookStatus(data: HttpBookInfo): BookStatusName {
  if (data.bookStatusName === "취소") {
    return BookStatusName.CANCEL;
  }
  if (data?.realOnDtm) {
    return BookStatusName.BOARD;
  }
  if (!data.pastYn) {
    return BookStatusName.BOOK;
  }
  return BookStatusName.NOSHOW;
}

function parseBoardingDate(dates: HttpBookDetailInfo[]): IBookDetailInfo[] {
  return dates.map((date) => {
    const bookDetailInfo: IBookDetailInfo = {
      bookDetailUid: date.bookDetailUid,
      boardingDate: dayjs(date.expectedOnDtm).format("YYYY-MM-DD HH:mm"),
      statusCode: date.statusCode,
      statusName: date.statusName,
    };
    return bookDetailInfo;
  });
}

@injectable()
export class BookInfoParser implements IBookInfoParser {
  constructor() {}

  toDomain(data: HttpBookInfo): Result<IBookInfo, ParseError> {
    try {
      const busLayoutSeat = data.busLayoutSeat;
      let seatsInfo = undefined;
      let mySeatFloor = 1;
      if (busLayoutSeat) {
        seatsInfo = parseSeatsInfo(busLayoutSeat.seats ?? []);
        mySeatFloor =
          seatsInfo.flat(3).find((seatInfo) => seatInfo.bookedByMe)?.floorNo ??
          1;
      }
      const bookInfoData: IBookInfo = {
        id: data.uid,
        bookDetailId: data.bookDetailUid,
        bookNum: data.bookDetailUid?.slice(-16, -3), // 202207151029523640276734951BKB -> 9523640276734951 (뒤 3자리 제외, 뒤에서부터 16자리)
        turnId: data.lineTurnUid,
        lineNum: data.lineRouteNo,
        routeName: data.lineRouteName,
        lineTypeCode: data.lineTypeCode,
        lineTypeName: useGetLineTypeName(data.lineTypeCode),
        bookSuccess:
          data.bookStatusName === "완료" || data.bookStatusName === "취소",
        bookFailedReason: data.note,
        bookStatus: parseBookStatus(data),
        bookDateTime: data.bookTs
          ? dayjs(data.bookTs).format("YYYY-MM-DD HH:mm:ss")
          : "",
        bookCancelAmount: data.cancelAmount,
        bookCancelDateTime: data.cancelTs
          ? dayjs(data.cancelTs).format("YYYY-MM-DD HH:mm:ss")
          : "",
        bookCancelEndDateTime: data.cancelEndDtm
          ? dayjs(data.cancelEndDtm).format("YYYY-MM-DD HH:mm")
          : "",
        boardDate: data.bookDate,
        boardingDates: data.details
          ? parseBoardingDate(data.details)
          : undefined,
        realBoardDate: data?.realOnDtm ?? "",
        firstStopTime: data.beginStationTimeText,
        getOnStopName: data.expectedOnStationName,
        getOnStopId: data.expectedOnStationUid,
        getOffStopName: data.expectedOffStationName,
        getOffStopId: data.expectedOffStationUid,
        departureTime: data.departureTimeText,
        arrivalTime: data.arrivalTimeText,
        seatNo: data.seatNo,
        floorNo: mySeatFloor,
        isPastBook: data.pastYn,
        bookAmount: data.bookAmount,
        pgCode: data.pgCode,
        bookIntervalCode: data.bookIntervalCode,
        seatAssignYn: data.seatAssignYn,
        allocUid: data.allocUid,
        carNo: data.carNo,
        vendorName: data.vendorName,
        seatsInfo: seatsInfo,
        remainAmount: data.remainAmount,
        cancelableAmount: data.cancelableAmount,
        cancelableYn: data.cancelableYn,
        penaltyAmount: data.penaltyAmount,
      };
      const bookInfo = new BookInfo(bookInfoData);
      return ok(bookInfo);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}
